import { ShoppingBag, Building2, TrendingUp, Laptop, Store, Heart, Landmark } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import caseStudyEcommerce from "@/assets/case-study-ecommerce.jpg";
import caseStudyB2B from "@/assets/case-study-b2b.jpg";
import caseStudySaas from "@/assets/case-study-saas.jpg";
import caseStudyRetail from "@/assets/case-study-retail.jpg";
import caseStudyHealth from "@/assets/case-study-health.jpg";
import caseStudyFinance from "@/assets/case-study-finance.jpg";

const CaseStudies = () => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  
  const cases = [
    {
      icon: Store,
      gradient: "from-rose-500 to-pink-600",
      image: caseStudyRetail,
      statValue: "+340%",
      statLabel: language === "fr" ? "avis Google" : "Google reviews",
      context: language === "fr" ? "Boutique de mode cherchant à booster sa réputation en ligne" : "Fashion boutique seeking to boost online reputation",
      result: language === "fr" ? "De 3.8 à 4.7 étoiles en 2 mois" : "From 3.8 to 4.7 stars in 2 months",
    },
    {
      icon: ShoppingBag,
      gradient: "from-amber-500 to-orange-600",
      image: caseStudyEcommerce,
      statValue: "+89%",
      statLabel: language === "fr" ? "followers Instagram" : "Instagram followers",
      context: language === "fr" ? "Épicerie fine souhaitant développer sa communauté locale" : "Gourmet grocery wanting to grow local community",
      result: language === "fr" ? "1500 nouveaux abonnés en 3 mois" : "1500 new followers in 3 months",
    },
    {
      icon: Heart,
      gradient: "from-emerald-500 to-green-600",
      image: caseStudyHealth,
      statValue: "+156%",
      statLabel: language === "fr" ? "recommandations" : "recommendations",
      context: language === "fr" ? "Salon de coiffure voulant augmenter le bouche-à-oreille" : "Hair salon wanting to increase word-of-mouth",
      result: language === "fr" ? "Clients recommandés x2 en 4 mois" : "Referred customers 2x in 4 months",
    },
    {
      icon: Building2,
      gradient: "from-rose-400 to-pink-500",
      image: caseStudyB2B,
      statValue: "+67%",
      statLabel: language === "fr" ? "taux de retour" : "return rate",
      context: language === "fr" ? "Restaurant gastronomique fidélisant sa clientèle premium" : "Fine dining restaurant retaining premium clientele",
      result: language === "fr" ? "Clients réguliers +67% en 6 mois" : "Regular customers +67% in 6 months",
    },
    {
      icon: Laptop,
      gradient: "from-teal-500 to-cyan-600",
      image: caseStudySaas,
      statValue: "+234%",
      statLabel: language === "fr" ? "avis positifs" : "positive reviews",
      context: language === "fr" ? "Spa & bien-être cherchant à dominer les avis locaux" : "Spa & wellness seeking to dominate local reviews",
      result: language === "fr" ? "N°1 des spas de la ville" : "#1 rated spa in the city",
    },
    {
      icon: Landmark,
      gradient: "from-indigo-500 to-blue-600",
      image: caseStudyFinance,
      statValue: "+124%",
      statLabel: language === "fr" ? "engagement" : "engagement",
      context: language === "fr" ? "Cabinet dentaire modernisant son approche client" : "Dental practice modernizing client approach",
      result: language === "fr" ? "Taux de recommandation x2" : "Referral rate doubled",
    },
  ];

  const CaseCard = ({ caseStudy, index }: { caseStudy: typeof cases[0]; index: number }) => (
    <div className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all duration-300 h-full">
      {/* Glass shine effect - subtle */}
      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none z-30">
        <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
      </div>
      {/* Image de fond */}
      <div className="relative h-32 overflow-hidden">
        <img 
          src={caseStudy.image} 
          alt={`Étude de cas - ${caseStudy.context}`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" />
        
        {/* Stat overlay */}
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg border border-slate-100">
          <div className={`text-lg font-display font-bold bg-gradient-to-r ${caseStudy.gradient} bg-clip-text text-transparent`}>
            {caseStudy.statValue}
          </div>
          <div className="text-[9px] text-slate-500">{caseStudy.statLabel}</div>
        </div>
        
        {/* Icon */}
        <div className={`absolute bottom-3 left-3 w-9 h-9 rounded-xl bg-gradient-to-br ${caseStudy.gradient} flex items-center justify-center shadow-lg`}>
          <caseStudy.icon className="h-4 w-4 text-white" />
        </div>
      </div>

      {/* Content compact */}
      <div className="p-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <span className="text-[9px] font-semibold text-rose-600 uppercase tracking-wide min-w-[45px]">
            {language === "fr" ? "Contexte" : "Context"}
          </span>
          <p className="text-[11px] text-slate-600 flex-1 line-clamp-2">
            {caseStudy.context}
          </p>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-[9px] font-semibold text-emerald-600 uppercase tracking-wide min-w-[45px]">
            {language === "fr" ? "Résultat" : "Result"}
          </span>
          <p className="text-[11px] font-medium text-slate-700 flex-1">
            {caseStudy.result}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <section id="cases" className="py-10 lg:py-12 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-rose-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-display font-bold text-slate-800">
            {language === "fr" ? "Ils nous font confiance" : "They trust us"}
          </h2>
        </div>
      </div>

      {/* Auto-scrolling marquee */}
      <div className="relative">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        
        <div className="flex gap-4 animate-marquee-left hover:[animation-play-state:paused]">
          {/* Double the items for seamless loop */}
          {[...cases, ...cases].map((caseStudy, index) => (
            <div key={index} className="flex-shrink-0 w-[300px]">
              <CaseCard caseStudy={caseStudy} index={index % cases.length} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
